import { Box, Paper, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

/** Tarjeta de indicador. Extraída de `reports.page.tsx` sin cambios. */

export interface KpiCardProps {
  icon: React.ReactNode;
  label: string;
  value: string;
  sub?: string;
  variant?: 'default' | 'warning' | 'success';
  isDark: boolean;
  onClick?: () => void;
}

export function KpiCard({
  icon,
  label,
  value,
  sub,
  variant = 'default',
  isDark,
  onClick,
}: KpiCardProps) {
  const { t } = useTranslation('sis');
  const colors = {
    default: {
      bg: isDark ? 'rgba(99,102,241,0.07)' : 'rgba(99,102,241,0.05)',
      border: isDark ? 'rgba(99,102,241,0.2)' : 'rgba(99,102,241,0.15)',
      icon: 'primary.main',
    },
    success: {
      bg: isDark ? 'rgba(34,197,94,0.07)' : 'rgba(34,197,94,0.05)',
      border: isDark ? 'rgba(34,197,94,0.2)' : 'rgba(34,197,94,0.15)',
      icon: 'success.main',
    },
    warning: {
      bg: isDark ? 'rgba(245,158,11,0.08)' : 'rgba(245,158,11,0.06)',
      border: isDark ? 'rgba(245,158,11,0.3)' : 'rgba(245,158,11,0.2)',
      icon: 'warning.main',
    },
  };
  const c = colors[variant];
  return (
    <Paper
      variant="outlined"
      onClick={onClick}
      sx={{
        flex: '1 1 160px',
        minWidth: 140,
        p: 2,
        borderRadius: 2,
        bgcolor: c.bg,
        borderColor: c.border,
        transition: 'transform 0.15s, box-shadow 0.15s',
        cursor: onClick ? 'pointer' : 'default',
        '&:hover': { transform: 'translateY(-2px)', boxShadow: 4 },
      }}
    >
      <Stack direction="row" alignItems="center" gap={1} mb={1}>
        <Box sx={{ color: c.icon, display: 'flex', fontSize: 18 }}>{icon}</Box>
        <Typography
          variant="caption"
          color="text.secondary"
          fontWeight={600}
          sx={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.06em' }}
        >
          {label}
        </Typography>
      </Stack>
      <Typography
        variant="h5"
        fontWeight={800}
        color={variant === 'warning' ? 'warning.main' : 'text.primary'}
      >
        {value}
      </Typography>
      {sub && (
        <Typography variant="caption" color="text.secondary">
          {sub}
        </Typography>
      )}
      {onClick && (
        <Typography
          variant="caption"
          sx={{
            color: 'primary.main',
            display: 'block',
            mt: 0.5,
            fontSize: 10,
            letterSpacing: '0.04em',
          }}
        >
          {t('reports.financial.kpis.breakdown')}
        </Typography>
      )}
    </Paper>
  );
}
